import { budgetColumns, type KpiColumn } from '../../audit/kpis';
import { appliesTo } from '../../audit/assignment';
import { canSeeStore } from '../../data/scope';
import { familyKey, groupFamilies, type TemplateFamily, type TemplateRow } from '../../data/templateAdmin';
import type { AppUser, Store } from '../../data/types';

/**
 * Who may change a store's budgets (B8, owner ruling 2026-10-25): Admins any
 * store, DMs the stores they can see. The server enforces the same rule
 * (api/src/writePolicy.ts) — this only decides whether inputs are shown.
 */
export function canEditBudgets(user: AppUser | null, store: Store): boolean {
  if (!user) return false;
  if (user.role === 'Admin') return true;
  return user.role === 'DM' && canSeeStore(user, store);
}

/** Only Admins set how a KPI is measured (company-wide, on the template). */
export const canEditKpiSettings = (user: AppUser | null) => user?.role === 'Admin';

export interface BudgetTemplate {
  family: TemplateFamily;
  /** The key budgets are stored under (the normalized AuditType). */
  key: string;
  name: string;
  columns: KpiColumn[];
}

/**
 * Every LIVE form, by name, with its budget / goal questions (owner ask
 * 2026-10-26: Budgets and Goals picks the form; a form without any shows
 * "No budgets or goal questions were found for this form"). `columns` =
 * src/audit/kpis.ts budgetColumns — may be empty.
 */
export function budgetTemplates(rows: readonly TemplateRow[]): BudgetTemplate[] {
  return groupFamilies(rows)
    .filter((f) => f.live !== null)
    .map((f) => ({ family: f, key: familyKey(f.auditType), name: f.name, columns: budgetColumns(f.rows) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The form Budgets and Goals opens on: the DM Visit (where KPIs are typed), else the first with budget questions. */
export function defaultBudgetTemplate(templates: readonly BudgetTemplate[]): BudgetTemplate | null {
  return templates.find((t) => t.key === 'dm_visit') ?? templates.find((t) => t.columns.length > 0) ?? templates[0] ?? null;
}

/** The forms WITH budget / goal questions that apply to one store (its assignment) — the store page's section. */
export function budgetTemplatesFor(rows: readonly TemplateRow[], store: Store): BudgetTemplate[] {
  return budgetTemplates(rows).filter((t) => t.columns.length > 0 && appliesTo(t.family.live!.assignment, store));
}
